import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import UnifiedCard from './ui/UnifiedCard';

const OpenAILogo = () => (
    <svg viewBox="0 0 24 24" className="w-12 h-12" fill="currentColor">
        <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.8956zm16.5963 3.8558L13.1038 8.364l2.0201-1.1638a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.4043-.6813zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6099-1.4997Z"/>
    </svg>
);

const techStack = [
    {
        category: 'AI & Development',
        items: [
            { name: 'Claude', description: 'AI assistant for coding and reasoning', link: 'https://claude.ai', imageUrl: 'https://cdn.prod.website-files.com/67ce28cfec624e2b733f8a52/68309ab48369f7ad9b4a40e1_open-graph.jpg' },
            { name: 'Wispr Flow', description: 'Voice-to-text dictation for fast writing', link: 'https://wisprflow.ai/r?AMANDA2959', imageUrl: 'https://cdn.prod.website-files.com/682f84b3838c89f8ff7667db/6a724a4fff322698a57a7d97_dictation-og%20(1).jpg' },
            { name: 'OpenAI API', description: 'GPT models for AI features', link: 'https://openai.com', logo: OpenAILogo },
            { name: 'Firebase', description: 'Backend, auth, and database', link: 'https://firebase.google.com', imageUrl: 'https://firebase.google.com/static/images/brand-guidelines/logo-logomark.png' },
            { name: 'Expo', description: 'React Native development', link: 'https://expo.dev', imageUrl: 'https://static.expo.dev/static/home/2026/og-image.png' },
            { name: 'VS Code', description: 'Code editor', link: 'https://code.visualstudio.com', imageUrl: 'https://code.visualstudio.com/opengraphimg/opengraph-home.png' },
        ],
    },
    {
        category: 'Productivity & Business',
        items: [
            { name: 'Notion', description: 'Notes, docs, project management', link: 'https://notion.so', imageUrl: 'https://www.notion.com/front-static/meta/teams-and-agents.jpg' },
            { name: 'Copilot', description: 'AI-powered financial management', link: 'https://copilot.money/download', imageUrl: 'https://framerusercontent.com/images/6R8aNZPKF0dsZXvptpAfjjpWzk.png', affiliateCode: 'FJ4GYC' },
            { name: 'Slack', description: 'Team communication', link: 'https://slack.com', imageUrl: 'https://a.slack-edge.com/737c9d1/marketing/img/homepage/revamped-24/unfurl/hp-revamp-unfurl.en-GB.jpg' },
            { name: 'Audible', description: 'Audiobooks for learning on the go', link: 'https://www.audible.com', imageUrl: 'https://m.media-amazon.com/images/G/01/Audible/en_US/images/Facebook_Placement_v1_OG_Tag.jpg' },
            { name: 'NotebookLM', description: 'AI-powered research and note-taking', link: 'https://notebooklm.google.com', imageUrl: 'https://notebook.google/_/static/branding/v6/og/notebook_lm_share.png' },
        ],
    },
    {
        category: 'Health & Fitness',
        items: [
            { name: 'MacroFactor', description: 'Smart macro tracking and nutrition', link: 'https://macrofactorapp.com', imageUrl: 'https://macrofactor.com/wp-content/uploads/2025/09/Website-Featured-Image.png' },
        ],
    },
    {
        category: 'Hardware & Home',
        items: [
            { name: 'Starlink', description: 'High-speed satellite internet', link: 'https://starlink.com/?referral=RC-877628-25691-28&app_source=share', imageUrl: 'https://starlink.com/assets-new/images/landing/field/v4_night_sky.webp' },
            { name: 'Tesla Model Y', description: 'Electric vehicle', link: 'https://ts.la/amanda817404', imageUrl: 'https://pbs.twimg.com/profile_images/1337607516008501250/6Ggc4S5n_400x400.png' },
            { name: 'MacBook Pro', description: 'Primary development machine', link: 'https://amzn.to/3UHFyZR', imageUrl: 'https://m.media-amazon.com/images/I/61lsexTCOhL._AC_SX679_.jpg' },
        ],
    },
];


const TechStackPage = () => {
    return (
        <HelmetProvider>
            <div className="flex-1 bg-cream text-ink">
                <Helmet prioritizeSeoTags>
                    <title>Tech Stack — Amanda Filomena | Tools I Use</title>
                    <link rel="canonical" href="https://amandafilomena.com/stack" />
                    <meta name="description" content="The tech tools, apps, and hardware I use daily as a founder and AI engineer. From development to productivity to life." />
                </Helmet>

                <main className="mx-auto max-w-6xl py-20 md:py-28">
                    {/* Hero */}
                    <div className="mb-16 px-6 opacity-0 animate-fade-up">
                        <p className="text-xs font-medium tracking-[0.2em] text-terra uppercase mb-4">What I Use</p>
                        <h1 className="text-3xl md:text-4xl font-medium tracking-tight text-ink mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
                            Tech Stack
                        </h1>
                        <p className="text-base text-muted max-w-2xl leading-relaxed">
                            The tools, apps, and hardware I actually use every day as a founder and engineer. Some have referral links.
                        </p>
                    </div>

                    {/* Stack Sections */}
                    <div className="space-y-12">
                        {techStack.map((category) => (
                            <div key={category.category} className="opacity-0 animate-fade-up">
                                <h2 className="text-xs font-medium uppercase tracking-[0.15em] text-terra mb-6 px-6">
                                    {category.category}
                                </h2>
                                <div className="relative w-full overflow-x-auto no-scrollbar">
                                    <div className="flex gap-4 px-6 pb-4">
                                        {category.items.map((item) => (
                                            <UnifiedCard
                                                key={item.name}
                                                name={item.name}
                                                subtitle={item.description}
                                                link={item.link}
                                                imageUrl={item.imageUrl}
                                                affiliateCode={item.affiliateCode}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* CTA */}
                    <section className="mt-24 mx-6 bg-ink p-10 md:p-14 text-center">
                        <p className="text-xs font-medium tracking-[0.2em] text-terra uppercase mb-4">Stay Connected</p>
                        <h2 className="text-2xl font-medium text-cream md:text-3xl mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
                            Want more recommendations?
                        </h2>
                        <p className="text-sm text-cream/60 mb-8 max-w-lg mx-auto">
                            I share new tools and what's working on Instagram. Follow along.
                        </p>
                        <a
                            href="https://www.instagram.com/amandafilomena_/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-terra px-7 py-3.5 text-sm font-medium text-white transition-all hover:bg-terra-dark tracking-wide"
                        >
                            @amandafilomena_
                        </a>
                    </section>
                </main>

                <footer className="border-t border-sand px-6 py-8">
                    <div className="mx-auto max-w-6xl flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <p className="text-sm text-muted">© 2026 Amanda Filomena</p>
                        <div className="flex gap-6">
                            <a href="https://github.com/amandafc-BYU" target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-wider text-muted hover:text-terra transition-colors">
                                GitHub
                            </a>
                            <a href="mailto:hello@amandafilomena.com" className="text-xs uppercase tracking-wider text-muted hover:text-terra transition-colors">
                                Contact
                            </a>
                        </div>
                    </div>
                </footer>
            </div>
        </HelmetProvider>
    );
};

export default TechStackPage;
